import React, { useEffect, useState } from "react";
import { api, fmtDate, todayISO, mediaUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Wrench, Plus, CalendarX, Trash2, CalendarClock, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ReservationDialog from "@/components/ReservationDialog";
import { toast } from "sonner";

const SEV_BADGE = { low: "badge-neutral", medium: "badge-pending", high: "badge-pending", critical: "badge-rejected" };
const STATUS_BADGE = { open: "badge-pending", in_progress: "badge-protected", resolved: "badge-approved", closed: "badge-neutral" };

export default function Maintenance() {
  const t = useT();
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reminderMenu, setReminderMenu] = useState(null); // ticket id whose menu is open
  const [sendingReminder, setSendingReminder] = useState(null);

  const reload = async () => {
    const [tk, a] = await Promise.all([api.get("/maintenance"), api.get("/assets")]);
    setTickets(tk.data); setAssets(a.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered =
    tab === "all" ? tickets :
    tab === "open" ? tickets.filter(tk => tk.status === "open" || tk.status === "in_progress") :
    tab === "planned" ? tickets.filter(tk => tk.is_planned) :
    tab === "reactive" ? tickets.filter(tk => !tk.is_planned) :
    tickets.filter(tk => tk.status === "resolved" || tk.status === "closed");

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/maintenance/${id}`, { status });
      toast.success(`Ticket ${status}`); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/maintenance/${id}`);
      toast.success("Ticket deleted"); setConfirmDelete(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendReminder = async (ticketId, kind) => {
    setSendingReminder(ticketId);
    setReminderMenu(null);
    try {
      const { data } = await api.post(`/maintenance/${ticketId}/remind`, { kind, channels: ["sms", "email"] });
      const smsOk = data.results?.sms?.ok;
      const emailOk = data.results?.email?.ok;
      const anyOk = smsOk || emailOk;
      const allOk = (data.results?.sms === null || smsOk) && (data.results?.email === null || emailOk);
      if (allOk && anyOk) toast.success(t("maint.reminderSent", { kind: t(`maint.reminder${kind === "long" ? "Long" : "Short"}`) }));
      else if (anyOk) toast.warning(t("maint.reminderPartial"));
      else {
        const msg = [data.results?.sms?.message, data.results?.email?.message].filter(Boolean).join(" · ");
        toast.error(msg || t("maint.reminderFailed"));
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder failed");
    } finally {
      setSendingReminder(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("maint.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("maint.title")}</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowBlock(true)} data-testid="block-asset-btn"
            className="bg-[#0B1221] border border-[#EF4444]/50 hover:bg-[#EF4444]/10 text-[#F87171]">
            <CalendarX className="w-4 h-4 mr-1" /> {t("maint.blockAsset")}
          </Button>
          <Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            <Plus className="w-4 h-4 mr-1" /> {t("maint.new")}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { k: "open", lk: "maint.tab.open" },
          { k: "planned", lk: "maint.tab.planned" },
          { k: "reactive", lk: "maint.tab.reactive" },
          { k: "done", lk: "maint.tab.done" },
          { k: "all", lk: "maint.tab.all" },
        ].map(({ k, lk }) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`maint-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs border ${tab === k ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#0B1221] border-[#1E293B] text-[#94A3B8]"}`}>{t(lk)}</button>
        ))}
      </div>

      {loading ? <div className="h-64 ledger-card animate-pulse" /> :
        filtered.length === 0 ? (
          <div className="ledger-card p-12 text-center text-[#94A3B8]">
            <Wrench className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">{t("maint.empty")}</div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(tk => (
              <div key={tk.id} className="ledger-card p-4" data-testid={`ticket-${tk.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`badge-pill ${STATUS_BADGE[tk.status]}`}>{t(`status.${tk.status}`)}</span>
                      <span className={`badge-pill ${SEV_BADGE[tk.severity]}`}>{t(`severity.${tk.severity}`)}</span>
                      {tk.is_planned ? (
                        <span className="badge-pill badge-protected"><CalendarClock className="w-3 h-3" /> {t("maint.plannedBadge")}</span>
                      ) : (
                        <span className="badge-pill badge-neutral">{t("maint.reactiveBadge")}</span>
                      )}
                      {tk.auto_reminder_enabled && (tk.provider_email || tk.provider_phone) && (
                        <span className="badge-pill badge-approved" title={t("maint.autoReminderHint")}>
                          <Send className="w-3 h-3" /> {t("maint.autoOn")}
                        </span>
                      )}
                      <span className="font-mono text-[10px] text-[#94A3B8]">{assets.find(a => a.id === tk.asset_id)?.name || "—"}</span>
                    </div>
                    <div className="font-medium">{tk.title}</div>
                    <div className="text-xs text-[#94A3B8] mt-1">{tk.description || "—"}</div>
                    <div className="flex flex-wrap gap-3 mt-2 font-mono text-[10px] text-[#94A3B8]">
                      <span>{t("maint.opened", { date: fmtDate(tk.opened_at), by: tk.reported_by_name })}</span>
                      {tk.scheduled_for && <span className="text-[#4DA6FF]"><CalendarClock className="w-3 h-3 inline mr-1" />{t("maint.scheduled", { date: fmtDate(tk.scheduled_for) })}{tk.scheduled_end ? ` → ${fmtDate(tk.scheduled_end)}` : ""}</span>}
                      {tk.service_provider && <span>· {tk.service_provider}</span>}
                      {tk.last_reminder_at && (
                        <span className="text-[#34D399]">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />
                          {t("maint.lastReminder", { kind: t(`maint.reminder${tk.last_reminder_kind === "short" ? "Short" : "Long"}`), date: fmtDate(tk.last_reminder_at.slice(0, 10)) })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    {tk.is_planned && tk.scheduled_for && (tk.provider_email || tk.provider_phone) && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setReminderMenu(reminderMenu === tk.id ? null : tk.id)}
                          disabled={sendingReminder === tk.id}
                          data-testid={`ticket-remind-btn-${tk.id}`}
                          className="bg-[#0B1221] border border-[#4DA6FF]/50 hover:bg-[#4DA6FF]/10 text-[#4DA6FF] h-8"
                        >
                          <Send className="w-3 h-3 mr-1" /> {sendingReminder === tk.id ? "…" : t("maint.sendReminder")}
                        </Button>
                        {reminderMenu === tk.id && (
                          <div className="absolute right-0 top-full mt-1 z-50 bg-[#0B1221] border border-[#1E293B] rounded-md shadow-lg min-w-[260px]" data-testid={`reminder-menu-${tk.id}`}>
                            <button onClick={() => sendReminder(tk.id, "long")}
                              data-testid={`reminder-send-long-${tk.id}`}
                              className="w-full text-left px-3 py-2 hover:bg-[#131C31] text-xs border-b border-[#1E293B] flex items-start gap-2">
                              <Send className="w-3 h-3 mt-0.5 text-[#4DA6FF]" />
                              <div>
                                <div className="font-medium">{t("maint.reminderLong")}</div>
                                <div className="text-[10px] text-[#94A3B8] font-mono">5 working days ahead</div>
                              </div>
                            </button>
                            <button onClick={() => sendReminder(tk.id, "short")}
                              data-testid={`reminder-send-short-${tk.id}`}
                              className="w-full text-left px-3 py-2 hover:bg-[#131C31] text-xs flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-0.5 text-[#FBBF24]" />
                              <div>
                                <div className="font-medium">{t("maint.reminderShort")}</div>
                                <div className="text-[10px] text-[#94A3B8] font-mono">1 day before</div>
                              </div>
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {(tk.status === "open") && (
                      <Button size="sm" onClick={() => updateStatus(tk.id, "in_progress")} data-testid={`ticket-start-${tk.id}`} className="bg-[#007AFF] hover:bg-[#0056B3] h-8">{t("maint.start")}</Button>
                    )}
                    {(tk.status === "open" || tk.status === "in_progress") && (
                      <Button size="sm" onClick={() => updateStatus(tk.id, "resolved")} data-testid={`ticket-resolve-${tk.id}`} className="bg-[#10B981] hover:bg-[#059669] h-8">{t("maint.resolve")}</Button>
                    )}
                    <button onClick={() => setConfirmDelete(tk.id)} data-testid={`ticket-delete-${tk.id}`}
                      className="p-1.5 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#EF4444] text-[#F87171]" title={t("common.delete")}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {tk.completion_notes && (
                  <div className="mt-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-md p-3" data-testid={`ticket-completion-${tk.id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#34D399]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#34D399]">{t("maint.completion.label")}</span>
                      {tk.completed_at && <span className="font-mono text-[10px] text-[#94A3B8]">{fmtDate(tk.completed_at.slice(0, 10))}{tk.completed_by_name ? ` · ${tk.completed_by_name}` : ""}</span>}
                    </div>
                    <div className="text-xs text-[#F8FAFC] whitespace-pre-wrap">{tk.completion_notes}</div>
                    {(tk.completion_photo_ids || []).length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                        {tk.completion_photo_ids.map((fid, idx) => (
                          <a key={fid} href={mediaUrl(fid)} target="_blank" rel="noopener noreferrer"
                            className="aspect-video bg-[#050A14] border border-[#1E293B] rounded-md overflow-hidden block hover:border-[#34D399]/50">
                            <img src={mediaUrl(fid)} alt={`work ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {confirmDelete === tk.id && (
                  <div className="mt-3 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md p-3" data-testid={`confirm-delete-ticket-${tk.id}`}>
                    <div className="text-xs text-[#F87171] mb-2">{t("maint.confirmDelete")}</div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => remove(tk.id)} data-testid={`confirm-delete-ticket-btn-${tk.id}`} className="h-7 flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-xs">{t("calendar.detail.yesDelete")}</Button>
                      <Button size="sm" onClick={() => setConfirmDelete(null)} className="h-7 flex-1 bg-[#050A14] border border-[#1E293B] hover:bg-[#131C31] text-xs">{t("common.keep")}</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }

      {showNew && <NewTicketDialog assets={assets} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      {showBlock && (
        <ReservationDialog
          open
          onOpenChange={(v) => !v && setShowBlock(false)}
          assets={assets}
          defaultDate={todayISO()}
          defaultType="maintenance_block"
          onSaved={() => { setShowBlock(false); toast.success("Asset period blocked"); reload(); }}
        />
      )}
    </div>
  );
}

function NewTicketDialog({ assets, onClose, onSaved }) {
  const t = useT();
  const [form, setForm] = useState({
    asset_id: assets[0]?.id || "",
    title: "", description: "", severity: "medium",
    is_planned: false, scheduled_for: "", scheduled_end: "", service_provider: "",
    provider_email: "", provider_phone: "", auto_reminder_enabled: false,
    reminder_language: "en",
  });
  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      const body = { ...form };
      ["scheduled_for", "scheduled_end", "service_provider", "provider_email", "provider_phone"].forEach(k => {
        if (!body[k]) delete body[k];
      });
      await api.post("/maintenance", body);
      toast.success("Ticket created"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const canAutoRemind = form.provider_email || form.provider_phone;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto max-w-xl" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{t("maint.dialog.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.asset")}</Label>
            <select value={form.asset_id} onChange={(e) => setForm(f => ({ ...f, asset_id: e.target.value }))}
              data-testid="ticket-asset-select"
              className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.titleField")}</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="ticket-title-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.severity")}</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {["low", "medium", "high", "critical"].map(s => (
                <button key={s} type="button" onClick={() => setForm(f => ({ ...f, severity: s }))}
                  data-testid={`ticket-severity-${s}`}
                  className={`px-3 py-2 rounded-md border text-xs capitalize ${form.severity === s ? "bg-[#EF4444]/15 border-[#EF4444]/50" : "bg-[#050A14] border-[#1E293B]"}`}>
                  {t(`severity.${s}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#050A14] border border-[#1E293B] rounded-md p-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><CalendarClock className="w-3 h-3 text-[#4DA6FF]" /> {t("maint.dialog.planned")}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">{t("maint.dialog.plannedHint")}</div>
            </div>
            <Switch
              checked={form.is_planned}
              onCheckedChange={(v) => setForm(f => ({ ...f, is_planned: v }))}
              data-testid="ticket-planned-switch"
            />
          </div>
          {form.is_planned && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.scheduledFor")}</Label>
                  <Input type="date" value={form.scheduled_for} onChange={(e) => setForm(f => ({ ...f, scheduled_for: e.target.value }))}
                    data-testid="ticket-scheduled-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.scheduledEnd")}</Label>
                  <Input type="date" value={form.scheduled_end} min={form.scheduled_for} onChange={(e) => setForm(f => ({ ...f, scheduled_end: e.target.value }))}
                    data-testid="ticket-scheduled-end-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
                  <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{t("maint.dialog.scheduledEndHint")}</div>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.serviceProvider")}</Label>
                <Input value={form.service_provider} onChange={(e) => setForm(f => ({ ...f, service_provider: e.target.value }))}
                  data-testid="ticket-provider-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.providerEmail")}</Label>
                  <Input type="email" value={form.provider_email} onChange={(e) => setForm(f => ({ ...f, provider_email: e.target.value }))}
                    placeholder="service@marina-piran.si"
                    data-testid="ticket-provider-email-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.providerPhone")}</Label>
                  <Input value={form.provider_phone} onChange={(e) => setForm(f => ({ ...f, provider_phone: e.target.value }))}
                    placeholder="+38631000000"
                    data-testid="ticket-provider-phone-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
                  <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{t("maint.providerPhoneHint")}</div>
                </div>
              </div>
              <div className={`flex items-center justify-between rounded-md p-3 border ${canAutoRemind ? "bg-[#050A14] border-[#1E293B]" : "bg-[#F59E0B]/10 border-[#F59E0B]/30"}`}>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2"><Send className="w-3 h-3 text-[#34D399]" /> {t("maint.autoReminder")}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-1">{t("maint.autoReminderHint")}</div>
                  {!canAutoRemind && (
                    <div className="text-[11px] text-[#FBBF24] mt-1 font-mono">{t("maint.noContactHint")}</div>
                  )}
                </div>
                <Switch
                  checked={form.auto_reminder_enabled}
                  disabled={!canAutoRemind}
                  onCheckedChange={(v) => setForm(f => ({ ...f, auto_reminder_enabled: v }))}
                  data-testid="ticket-auto-reminder-switch"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maintenance.reminderLanguage")}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["en", "sl"].map(l => (
                    <button
                      key={l} type="button"
                      onClick={() => setForm(f => ({ ...f, reminder_language: l }))}
                      data-testid={`ticket-reminder-lang-${l}`}
                      className={`px-3 py-2 rounded-md border text-xs ${form.reminder_language === l ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8]"}`}
                    >
                      {t(`maintenance.reminderLanguage.${l}`)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("maint.dialog.description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} data-testid="ticket-desc-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} data-testid="ticket-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
